import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { MessageSquare, Users, Search, Plus, Upload, X, Edit2, Eye, UserMinus } from 'lucide-react';
import ContactList from './ContactList';
import ChatInterface from './ChatInterface';
import GroupDetailsView from './GroupDetailsView';
import ContactGroupList from '../contact/ContactGroupList';
import ContactModal from '../contact/ContactModal';
import ExcelUploadModal from '../contact/ExcelUploadModal';
import GroupCreateModal from '../contact/GroupCreateModal';
import AddContactsToGroupModal from '../contact/AddContactsToGroupModal';
import ConfirmModal from '../common/ConfirmModal';
import { contactService } from '../../services/contactService';
import { contactGroupService } from '../../services/contactGroupService';
import toast from 'react-hot-toast';

const UnifiedChatLayout = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('contacts'); // 'contacts' or 'groups'
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [showContactList, setShowContactList] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Contact management state
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [contactGroups, setContactGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupContacts, setGroupContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  
  // Modal states
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showDeleteContactModal, setShowDeleteContactModal] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [showRemoveFromGroupModal, setShowRemoveFromGroupModal] = useState(false);
  const [showAddContactsModal, setShowAddContactsModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [contactToRemoveFromGroup, setContactToRemoveFromGroup] = useState(null);
  const groupDetailsRefreshRef = useRef(null);

  useEffect(() => {
    if (activeTab === 'groups') {
      loadContactGroups();
    }
  }, [activeTab]);

  useEffect(() => {
    const selectedFromRoute = location.state?.contactId;
    if (!selectedFromRoute) {
      return;
    }
    setActiveTab('contacts');
    setSelectedContactId(selectedFromRoute);
    if (window.innerWidth < 768) {
      setShowContactList(false);
    }
  }, [location.state]);

  // Clear selections when switching tabs
  useEffect(() => {
    setSelectedContactId(null);
    setSelectedGroup(null);
    setShowContactList(true); // Show contact list on desktop
  }, [activeTab]);

  const loadContactGroups = async () => {
    if (!user?.customerId) return;
    
    setLoadingGroups(true);
    try {
      const response = await contactService.getContactGroups(user.customerId);
      setContactGroups(response || []);
    } catch (error) {
      console.error('Error loading contact groups:', error);
      toast.error('Failed to load contact groups');
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleContactSelect = (contactId) => {
    setSelectedContactId(contactId);
    setSelectedGroup(null); // Clear selected group when selecting contact
    if (window.innerWidth < 768) {
      setShowContactList(false);
    }
  };

  const handleBackToList = () => {
    setShowContactList(true);
    setSelectedContactId(null);
    setSelectedGroup(null); // Clear selected group when going back
  };

  const handleBackFromGroup = () => {
    setSelectedGroup(null);
    setShowContactList(true);
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setShowContactModal(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setShowContactModal(true);
  };

  const handleDeleteContact = (contact) => {
    setContactToDelete(contact);
    setShowDeleteContactModal(true);
  };

  const confirmDeleteContact = async () => {
    if (!contactToDelete) return;

    try {
      await contactService.deleteContact(contactToDelete.contactId);
      toast.success('Contact deleted successfully');
      setShowDeleteContactModal(false);
      setContactToDelete(null);
      if (selectedGroup && groupDetailsRefreshRef.current) {
        await groupDetailsRefreshRef.current();
      }
      if (activeTab === 'groups') {
        loadContactGroups();
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const handleRemoveContactFromGroup = (contact) => {
    setContactToRemoveFromGroup(contact);
    setShowRemoveFromGroupModal(true);
  };

  const confirmRemoveContactFromGroup = async () => {
    if (!contactToRemoveFromGroup || !selectedGroup) return;

    try {
      const contactId = contactToRemoveFromGroup.contactId;
      const groupId = selectedGroup.groupId;
      
      console.log('Removing contact:', contactId, 'from group:', groupId);
      
      await contactGroupService.removeContactsFromGroup(groupId, [contactId]);
      toast.success('Contact removed from group successfully');
      setShowRemoveFromGroupModal(false);
      setContactToRemoveFromGroup(null);
      // Reload group contacts via refresh ref
      if (groupDetailsRefreshRef.current) {
        await groupDetailsRefreshRef.current();
      }
      // Reload groups list to update counts
      if (activeTab === 'groups') {
        loadContactGroups();
      }
    } catch (error) {
      console.error('Error removing contact from group:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to remove contact from group';
      toast.error(errorMessage);
    }
  };

  const handleViewGroupDetails = async (group) => {
    setSelectedGroup(group);
    setSelectedContactId(null); // Clear selected contact when viewing group
    if (window.innerWidth < 768) {
      setShowContactList(false);
    }
  };

  const handleAddContactsToGroup = (group) => {
    setSelectedGroup(group);
    setShowAddContactsModal(true);
  };

  const handleAddContactsSuccess = async () => {
    if (selectedGroup && groupDetailsRefreshRef.current) {
      // Refresh the group details view
      await groupDetailsRefreshRef.current();
      // Reload groups list to update counts
      if (activeTab === 'groups') {
        loadContactGroups();
      }
    }
  };

  const handleDownloadGroup = async (group) => {
    try {
      const blob = await contactService.downloadGroupContacts(group.groupId, user.customerId);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${group.groupName}_contacts.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${group.groupName} contacts`);
    } catch (error) {
      console.error('Error downloading group contacts:', error);
      toast.error('Failed to download group contacts');
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setShowEditGroupModal(true);
  };

  const onUpdateGroup = async (groupData) => {
    if (!editingGroup) return;

    try {
      await contactGroupService.updateContactGroup(editingGroup.groupId, groupData);
      toast.success('Group updated successfully');
      setShowEditGroupModal(false);
      setEditingGroup(null);
      loadContactGroups();
    } catch (error) {
      console.error('Error updating group:', error);
      throw error; // Let the modal handle the error
    }
  };

  const handleDeleteGroup = (group) => {
    setGroupToDelete(group);
    setShowDeleteGroupModal(true);
  };

  const confirmDeleteGroup = async () => {
    if (!groupToDelete) return;

    try {
      await contactGroupService.deleteContactGroup(groupToDelete.groupId);
      toast.success('Group deleted successfully');
      setShowDeleteGroupModal(false);
      setGroupToDelete(null);
      loadContactGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    }
  };

  const handleCreateGroup = () => {
    setShowCreateGroupModal(true);
  };

  const onCreateGroup = async (groupData) => {
    try {
      await contactGroupService.createContactGroup(groupData);
      toast.success('Group created successfully');
      loadContactGroups();
    } catch (error) {
      console.error('Error creating group:', error);
      throw error; // Let the modal handle the error
    }
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-gray-50">
      {/* Left Sidebar */}
      <div className={`${showContactList ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-1/3 lg:w-1/4 border-r border-gray-200 bg-white`}>
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary-600" />
              Messages
            </h2>
          </div>
          
          {/* Tabs */}
          <div className="flex mb-3 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('contacts')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'contacts'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Contacts
            </button>
            <button
              onClick={() => setActiveTab('groups')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'groups'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Groups
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 mb-3">
            {activeTab === 'contacts' ? (
              <button
                onClick={handleAddContact}
                className="flex-1 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors flex items-center justify-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            ) : (
              <>
                <button
                  onClick={handleCreateGroup}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors flex items-center justify-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Create Group
                </button>
                <button
                  onClick={() => setShowExcelModal(true)}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center gap-1"
                  title="Upload Excel"
                >
                  <Upload className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={activeTab === 'contacts' ? 'Search contacts...' : 'Search groups...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'contacts' ? (
            <ContactList
              searchTerm={searchTerm}
              selectedContactId={selectedContactId}
              onContactSelect={handleContactSelect}
              onEditContact={handleEditContact}
              onDeleteContact={handleDeleteContact}
            />
          ) : (
            <ContactGroupList
              contactGroups={contactGroups}
              loading={loadingGroups}
              onDownloadGroup={handleDownloadGroup}
              onViewGroupDetails={handleViewGroupDetails}
              onEditGroup={handleEditGroup}
              onDeleteGroup={handleDeleteGroup}
              onAddContacts={handleAddContactsToGroup}
              selectedGroup={selectedGroup}
            />
          )}
        </div>
      </div>

      {/* Right Panel - Chat Interface or Group Details */}
      <div className={`${!showContactList ? 'flex' : 'hidden'} md:flex flex-col flex-1 bg-gray-50`}>
        {selectedContactId ? (
          <ChatInterface
            contactId={selectedContactId}
            onBack={handleBackToList}
          />
        ) : selectedGroup ? (
          <GroupDetailsView
            group={selectedGroup}
            onBack={handleBackFromGroup}
            onContactSelect={handleContactSelect}
            onRemoveContact={handleRemoveContactFromGroup}
            onAddContacts={() => setShowAddContactsModal(true)}
            onRefresh={groupDetailsRefreshRef}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Select a contact or group to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showContactModal && (
        <ContactModal
          customerId={user?.customerId}
          contact={editingContact}
          onClose={() => {
            setShowContactModal(false);
            setEditingContact(null);
          }}
          onSaved={() => {
            setShowContactModal(false);
            setEditingContact(null);
            if (activeTab === 'groups') {
              loadContactGroups();
            }
            if (selectedGroup && groupDetailsRefreshRef.current) {
              groupDetailsRefreshRef.current();
            }
          }}
        />
      )}

      {showExcelModal && (
        <ExcelUploadModal
          customerId={user?.customerId}
          onClose={() => setShowExcelModal(false)}
          onSuccess={() => {
            setShowExcelModal(false);
            loadContactGroups();
            toast.success('Contacts uploaded successfully');
          }}
        />
      )}

      {/* Create Group Modal */}
      <GroupCreateModal
        isOpen={showCreateGroupModal}
        onClose={() => setShowCreateGroupModal(false)}
        onCreate={onCreateGroup}
      />

      {/* Edit Group Modal */}
      <GroupCreateModal
        isOpen={showEditGroupModal}
        onClose={() => {
          setShowEditGroupModal(false);
          setEditingGroup(null);
        }}
        onCreate={onUpdateGroup}
        group={editingGroup}
      />

      {/* Delete Contact Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteContactModal}
        onClose={() => {
          setShowDeleteContactModal(false);
          setContactToDelete(null);
        }}
        onConfirm={confirmDeleteContact}
        title="Delete Contact"
        message={contactToDelete ? `Are you sure you want to delete contact "${contactToDelete.contactName || contactToDelete.phoneNumber}"? This action cannot be undone.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Delete Group Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteGroupModal}
        onClose={() => {
          setShowDeleteGroupModal(false);
          setGroupToDelete(null);
        }}
        onConfirm={confirmDeleteGroup}
        title="Delete Group"
        message={groupToDelete ? `Are you sure you want to delete the group "${groupToDelete.groupName}"? This will not delete the contacts, only remove them from this group.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Remove Contact from Group Confirmation Modal */}
      <ConfirmModal
        isOpen={showRemoveFromGroupModal}
        onClose={() => {
          setShowRemoveFromGroupModal(false);
          setContactToRemoveFromGroup(null);
        }}
        onConfirm={confirmRemoveContactFromGroup}
        title="Remove from Group"
        message={contactToRemoveFromGroup && selectedGroup ? `Are you sure you want to remove "${contactToRemoveFromGroup.contactName || contactToRemoveFromGroup.phoneNumber}" from the group "${selectedGroup.groupName}"? This will not delete the contact.` : ''}
        confirmText="Remove"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Add Contacts to Group Modal */}
      <AddContactsToGroupModal
        isOpen={showAddContactsModal}
        onClose={() => {
          setShowAddContactsModal(false);
        }}
        group={selectedGroup}
        onSuccess={handleAddContactsSuccess}
      />
    </div>
  );
};

export default UnifiedChatLayout;

